import { BookingChangePublicDto } from '@/features/dashboard/bookingChanges/types/dto/BookingChangePublicDto.ts';
import { PublicChangeItemDto } from '@/features/dashboard/bookingChanges/types/dto/BookingChangePublicDto.ts';
import { CustomFieldValueDto } from '@/types/CustomFieldValueDto.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { AcceptBookingChangeRequest } from '@/features/dashboard/bookingChanges/types/dto/AcceptBookingChangeRequest.ts';
import { RejectBookingChangeRequest } from '@/features/dashboard/bookingChanges/types/dto/RejectBookingChangeRequest.ts';
import { ChangeItemType } from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import {
  InsuranceChangeOp,
  OptionChangeOp,
  TransportChangeOp,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeOps.ts';
import {
  PendingChangeGroupVm,
  PendingChangeLineVm,
  PendingChangeVm,
} from '@/features/dashboard/bookingChanges/types/PublicChangeViewModel.ts';
import {
  PublicClientDto,
  PublicCostLineKind,
  PublicSummaryParticipantDto,
} from '@/features/public/bookingSummary/types/BookingPublicSummaryDto.ts';
import {
  formatDate,
  formatMoneyExact,
} from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { formatPhoneNumber } from '@/types/PhoneNumberDto.utils.ts';
import { fullNameOf } from '@/utils/personName.ts';
import { BOOKING_WIDE_GROUP_LABEL } from '@/features/dashboard/bookingChanges/utils/changeSummary.ts';

/** Grupa danych zapisującego — pozycje bez `participantId`. */
const CLIENT_GROUP_ID = 'client';
const CLIENT_GROUP_NAME = 'Twoje dane';
const BOOKING_GROUP_ID = 'booking';

/**
 * Osoba, której paczka dotyczy, ale której nie ma na liście uczestników
 * rezerwacji. W praktyce nie powinno się zdarzyć; gdyby jednak, wiersz ma
 * zostać widoczny, a nie zniknąć razem z nierozpoznanym identyfikatorem.
 */
const UNKNOWN_PERSON_NAME = 'Uczestnik';

const personNameLine = (
  id: string,
  firstName: string | null,
  lastName: string | null,
  previousName: string | null,
): PendingChangeLineVm | null => {
  const name = fullNameOf(firstName ?? '', lastName ?? '').trim();
  return name
    ? {
        id: `${id}:name`,
        label: 'Imię i nazwisko',
        value: name,
        previousValue: previousName,
      }
    : null;
};

/**
 * Wybór z katalogu jako jeden wiersz: nazwa rzeczy w etykiecie, wybór w wartości.
 *
 * Nazwa bywa pusta, gdy paczka wskazuje wariant zdjęty z oferty po jej
 * wystawieniu — wtedy zostaje sam fakt zmiany, bo UUID klientowi nic nie mówi.
 */
const catalogLine = (
  id: string,
  label: string,
  fallbackLabel: string,
  name: string | null,
  previousName: string | null = null,
): PendingChangeLineVm =>
  name
    ? { id, label, value: name, previousValue: previousName }
    : { id, label: fallbackLabel, value: null, previousValue: previousName };

/**
 * Pozycja zdejmowana z rezerwacji. Nie „rezygnacja” — tym słowem moduł nazywa
 * odejście uczestnika z wyjazdu (paczka `WITHDRAWAL`), a klient czytający, że
 * przy transporcie stoi „rezygnacja”, ma prawo pomyśleć, że traci wyjazd.
 * Forma bezrodzajowa, bo ten sam napis obsługuje transport, ubezpieczenie
 * i opcję dodatkową.
 */
const REMOVED_VALUE = 'do usunięcia';

/** Wartość pola niestandardowego w postaci czytelnej dla klienta. */
const customValueText = (
  value: CustomFieldValueDto,
  optionNames: Record<string, string>,
): string | null => {
  switch (value.type) {
    case FieldType.CHECKBOX:
      return value.value ? 'Tak' : 'Nie';
    case FieldType.DROPDOWN:
      // Wartością jest identyfikator opcji — bez etykiety lepiej nie pokazywać nic.
      return optionNames[value.value] ?? null;
    default:
      return value.value || null;
  }
};

/**
 * Wartości sprzed zmiany, wyciągnięte z podsumowania rezerwacji.
 *
 * Kontrakt zmian niesie sam stan docelowy — czym rzecz jest **dziś**, wie ta sama strona, na której
 * panel stoi. Dlatego „przed" bierze się ze złączenia obu, a nie z kolejnych pól kontraktu.
 * Wyjątkiem są wybory z katalogu (opcja, transport, ubezpieczenie): tam nazwę sprzed zmiany niesie
 * już sam kontrakt, bo wariant bywa wycofany z oferty i wtedy w rachunku klienta go nie ma.
 *
 * Każdy odczyt oddaje `null`, gdy nie ma czego pokazać — kreski się nie zmyśla.
 */
interface PreviousValues {
  personName: (participantId: string | null) => string | null;
  birthDate: (participantId: string | null) => string | null;
  basePrice: (participantId: string | null) => string | null;
  formAnswer: (participantId: string | null, fieldId: string) => string | null;
  clientName: () => string | null;
  clientEmail: () => string | null;
  clientPhone: () => string | null;
  clientBirthDate: () => string | null;
  clientAddress: () => string | null;
}

const previousValuesOf = (
  participants: PublicSummaryParticipantDto[],
  client: PublicClientDto | undefined,
): PreviousValues => {
  const byId = new Map(participants.map((p) => [p.id, p]));
  const of = (participantId: string | null) =>
    participantId ? byId.get(participantId) : undefined;

  return {
    personName: (id) => {
      const participant = of(id);
      return participant
        ? fullNameOf(participant.firstName, participant.lastName)
        : null;
    },
    birthDate: (id) => {
      const participant = of(id);
      return participant ? formatDate(participant.birthDate) : null;
    },
    // Uczestnik ma jedną cenę podstawową; gdyby kiedyś miał więcej, pierwsza i tak
    // jest tą, o której mówi pozycja paczki.
    basePrice: (id) => {
      const line = of(id)?.costLines.find(
        (costLine) => costLine.kind === PublicCostLineKind.BASE,
      );
      return line ? formatMoneyExact(line.amount) : null;
    },
    // Po identyfikatorze pola, nie po etykiecie: dopasowanie po napisie pękłoby
    // przy pierwszej zmianie nazwy pola w formularzu wyjazdu.
    formAnswer: (id, fieldId) =>
      of(id)?.formAnswers.find((answer) => answer.fieldId === fieldId)?.value ??
      null,
    clientName: () =>
      client ? fullNameOf(client.firstName, client.lastName) : null,
    clientEmail: () => client?.email ?? null,
    clientPhone: () =>
      client?.phoneNumber ? formatPhoneNumber(client.phoneNumber) : null,
    clientBirthDate: () => (client ? formatDate(client.birthDate) : null),
    clientAddress: () => {
      if (!client) {
        return null;
      }
      const line = [client.postalCode, client.city].filter(Boolean).join(' ');
      const parts = [client.street, line].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : null;
    },
  };
};

/** Wiersze jednej pozycji paczki. */
const linesOf = (
  item: PublicChangeItemDto,
  before: PreviousValues,
): PendingChangeLineVm[] => {
  switch (item.type) {
    case ChangeItemType.PARTICIPANT_DATA:
      return [
        personNameLine(
          item.id,
          item.newFirstName,
          item.newLastName,
          before.personName(item.participantId),
        ),
        item.newBirthDate && {
          id: `${item.id}:birth`,
          label: 'Data urodzenia',
          value: formatDate(item.newBirthDate),
          previousValue: before.birthDate(item.participantId),
        },
      ].filter((line): line is PendingChangeLineVm => !!line);

    case ChangeItemType.CLIENT_DATA:
      return [
        personNameLine(
          item.id,
          item.newFirstName,
          item.newLastName,
          before.clientName(),
        ),
        item.newEmail && {
          id: `${item.id}:email`,
          label: 'Adres e-mail',
          value: item.newEmail,
          previousValue: before.clientEmail(),
        },
        item.newPhoneNumber && {
          id: `${item.id}:phone`,
          label: 'Numer telefonu',
          value: formatPhoneNumber(item.newPhoneNumber),
          previousValue: before.clientPhone(),
        },
        item.newBirthDate && {
          id: `${item.id}:birth`,
          label: 'Data urodzenia',
          value: formatDate(item.newBirthDate),
          previousValue: before.clientBirthDate(),
        },
        item.newAddress && {
          id: `${item.id}:address`,
          label: 'Adres',
          value: `${item.newAddress.street}, ${item.newAddress.postalCode} ${item.newAddress.city}`,
          previousValue: before.clientAddress(),
        },
      ].filter((line): line is PendingChangeLineVm => !!line);

    case ChangeItemType.BASE_PRICE:
      return [
        {
          id: item.id,
          label: 'Cena podstawowa',
          value: item.amount ? formatMoneyExact(item.amount) : null,
          previousValue: before.basePrice(item.participantId),
        },
      ];

    case ChangeItemType.OPTION:
      if (item.op === OptionChangeOp.REMOVE) {
        return [
          {
            id: item.id,
            label: item.optionName ?? 'Opcja dodatkowa',
            value: REMOVED_VALUE,
            previousValue: item.previousVariantName,
          },
        ];
      }
      return [
        catalogLine(
          item.id,
          item.optionName ?? 'Opcja dodatkowa',
          item.op === OptionChangeOp.ADD
            ? 'Nowa opcja dodatkowa'
            : 'Zmiana opcji dodatkowej',
          item.newVariantName,
          item.previousVariantName,
        ),
      ];

    case ChangeItemType.TRANSPORT:
      if (item.op === TransportChangeOp.CLEAR) {
        return [
          {
            id: item.id,
            label: 'Transport',
            value: REMOVED_VALUE,
            previousValue: item.previousVariantName,
          },
        ];
      }
      return [
        catalogLine(
          item.id,
          'Transport',
          item.op === TransportChangeOp.SET
            ? 'Wybór transportu'
            : 'Zmiana transportu',
          item.newVariantName,
          item.previousVariantName,
        ),
      ];

    case ChangeItemType.INSURANCE:
      if (item.op === InsuranceChangeOp.CLEAR) {
        return [
          {
            id: item.id,
            label: 'Ubezpieczenie',
            value: REMOVED_VALUE,
            previousValue: item.previousInsuranceName,
          },
        ];
      }
      return [
        catalogLine(
          item.id,
          'Ubezpieczenie',
          item.op === InsuranceChangeOp.SET
            ? 'Wybór ubezpieczenia'
            : 'Zmiana ubezpieczenia',
          item.newInsuranceName,
          item.previousInsuranceName,
        ),
      ];

    case ChangeItemType.CUSTOM_VALUES: {
      const values = item.newCustomValues ?? {};
      const names = item.fieldNames ?? {};
      const optionNames = item.dropdownOptionNames ?? {};
      const lines = Object.entries(values)
        .filter(([fieldId]) => names[fieldId])
        .map(([fieldId, value]) => ({
          id: `${item.id}:${fieldId}`,
          label: names[fieldId],
          value: customValueText(value, optionNames),
          previousValue: before.formAnswer(item.participantId, fieldId),
        }));

      // Pole bez nazwy zostawiłoby wiersz opisany identyfikatorem, więc gdy
      // nazw brakuje w komplecie, zostaje jedno zdanie o formularzu.
      return lines.length > 0
        ? lines
        : [
            {
              id: item.id,
              label: 'Informacje z formularza zgłoszeniowego',
              value: null,
              previousValue: null,
            },
          ];
    }

    case ChangeItemType.MANUAL_LINE:
      return [
        {
          id: item.id,
          label: item.description,
          value: item.amount ? formatMoneyExact(item.amount) : null,
          previousValue: null,
        },
      ];

    default:
      return [];
  }
};

/**
 * Grupa pozycji. `participantId === null` niesie zarówno dane zapisującego,
 * jak i pozycję ręczną całej rezerwacji — ta druga danymi klienta nie jest.
 */
const groupOf = (
  item: PublicChangeItemDto,
  names: Map<string, string>,
): Pick<PendingChangeGroupVm, 'id' | 'personName'> => {
  if (item.participantId) {
    return {
      id: item.participantId,
      personName: names.get(item.participantId) ?? UNKNOWN_PERSON_NAME,
    };
  }
  return item.type === ChangeItemType.MANUAL_LINE
    ? { id: BOOKING_GROUP_ID, personName: BOOKING_WIDE_GROUP_LABEL }
    : { id: CLIENT_GROUP_ID, personName: CLIENT_GROUP_NAME };
};

/**
 * Kolejność grup jak w podglądzie operatora: uczestnicy w kolejności rezerwacji,
 * dane zapisującego, na końcu cała rezerwacja.
 */
const groupRank = (
  groupId: string,
  participants: PublicSummaryParticipantDto[],
): number => {
  if (groupId === BOOKING_GROUP_ID) {
    return participants.length + 2;
  }
  if (groupId === CLIENT_GROUP_ID) {
    return participants.length + 1;
  }
  const index = participants.findIndex(
    (participant) => participant.id === groupId,
  );
  return index === -1 ? participants.length : index;
};

/**
 * Mapper publicznej strony akceptacji — panel na podsumowaniu rezerwacji.
 *
 * Imion w kontrakcie zmian nie ma i nie dokładamy ich tam: strona czyta je
 * z `GET …/booking-summary` tym samym tokenem i slugiem, a złączenie idzie po
 * `participantId`. Nazw wariantów, wartości sprzed zmiany i kwot cząstkowych
 * wciąż brak (`PublicChangePresentationDto`), więc panel mówi tyle, ile kontrakt
 * pozwala powiedzieć uczciwie — pełną tabelę „przed → po” widzi tylko operator.
 */
export const bookingChangePublicMapper = {
  /**
   * Paczka w kształcie panelu — pozycje zebrane pod osobami, po jednej grupie
   * na uczestnika, jednej na dane zapisującego i jednej na pozycje całej
   * rezerwacji. Grupa bez wierszy nie powstaje.
   */
  toPendingChangeVm(
    dto: BookingChangePublicDto,
    participants: PublicSummaryParticipantDto[],
    client?: PublicClientDto,
  ): PendingChangeVm {
    const names = new Map(
      participants.map((participant) => [
        participant.id,
        fullNameOf(participant.firstName, participant.lastName),
      ]),
    );
    const before = previousValuesOf(participants, client);
    const groups = new Map<string, PendingChangeGroupVm>();

    dto.items.forEach((item) => {
      const lines = linesOf(item, before);
      if (lines.length === 0) {
        return;
      }
      const identity = groupOf(item, names);
      const group = groups.get(identity.id) ?? { ...identity, lines: [] };
      groups.set(identity.id, { ...group, lines: [...group.lines, ...lines] });
    });

    return {
      changeId: dto.id,
      balance: dto.additionalPayment,
      requiresAmendment: dto.requiresAmendment,
      groups: [...groups.values()].sort(
        (a, b) => groupRank(a.id, participants) - groupRank(b.id, participants),
      ),
    };
  },

  /**
   * Żądanie akceptacji: klient potwierdza kwotę, którą widział na stronie.
   * Rozjazd z kwotą po stronie serwera kończy się HTTP 409.
   */
  toAcceptRequest(dto: BookingChangePublicDto): AcceptBookingChangeRequest {
    return { expectedAdditionalPayment: dto.additionalPayment };
  },

  /** Komentarz przychodzi przycięty ze schematu formularza; pusty idzie jako `null`. */
  toRejectRequest(form: { comment: string }): RejectBookingChangeRequest {
    return { comment: form.comment || null };
  },
};
